import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, TextField, MenuItem, Alert, Typography } from '@mui/material';
import {
  FormShell,
  FormActions,
  SectionLabel,
  EmptyOption,
  useLocationPicker,
  inputSx,
} from '../FormUI/RoadAccidentFormUI';
import useMunicipalityCascade from '../FormUI/useMunicipalityCascade';
import MunicipalityFields from '../FormUI/MunicipalityFields';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const initialFormData = {
  type: '',
  location: '',
  latitude: '',
  longitude: '',
};

function DPambulance() {
  const navigate = useNavigate();
  const cascade = useMunicipalityCascade();
  const [formData, setFormData] = useState(initialFormData);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (latitude, longitude) => {
    setFormData((prev) => ({ ...prev, latitude, longitude }));
  };

  const handleLocationResolved = (adm) => {
    if (adm) cascade.applyResolvedAdm(adm.adm1_code, adm.adm2_code, adm.adm3_code);
  };

  const handleLandmark = (landmark) => {
    setFormData((prev) => ({ ...prev, location: landmark || '' }));
  };

  const { latLngFields, mapElement, locationError, landmarkNotFound } = useLocationPicker({
    latitude: formData.latitude,
    longitude: formData.longitude,
    onChange: handleLocationChange,
    onLocationResolved: handleLocationResolved,
    onLandmark: handleLandmark,
    infoText: 'Click on the map to set the ambulance station location.',
  });

  const handleReset = () => {
    setFormData(initialFormData);
    cascade.reset();
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleBackClick = () => {
    navigate('/Roadaccident/Reports?tab=ambulance');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    try {
      const response = await axios.post(`${backendUrl}roadaccident/submit_ambulance_details/`, {
        type: formData.type,
        municipality: cascade.selectedMunicipality,
        submunicipality: cascade.selectedSubMunicipality,
        suco: cascade.selectedSuco,
        location: formData.location,
        latitude: formData.latitude,
        longitude: formData.longitude,
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit. Please try again.');
      }
    } catch (error) {
      setSubmitError(`An error occurred while submitting: ${error.message}`);
    }
  };

  return (
    <FormShell title="Add Ambulance Record" subtitle="Register an ambulance contact point" onBack={handleBackClick} onSubmit={handleSubmit}>
      {submitSuccess && <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>Ambulance Details added successfully.</Alert>}
      {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

      <Box>
        <SectionLabel text="Contact" />
        <TextField
          label="Type"
          select
          size="small"
          fullWidth
          sx={inputSx}
          name="type"
          value={formData.type}
          onChange={handleChange}
        >
          <EmptyOption text="Select a type" />
          <MenuItem value="110">110</MenuItem>
          <MenuItem value="+670 331 1044">+670 331 1044</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
        {/* LEFT: Location Information */}
        <Box>
          <SectionLabel text="Location Information" />

          {locationError && !(cascade.selectedMunicipality && cascade.selectedSubMunicipality && cascade.selectedSuco) && (
            <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>
          )}

          <MunicipalityFields cascade={cascade} />
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              sx={inputSx}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location details"
            />
            {landmarkNotFound && !formData.location && (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {latLngFields}
          </Box>
        </Box>

        {/* RIGHT: Map */}
        <Box>{mapElement}</Box>
      </Box>

      <FormActions onReset={handleReset} submitLabel="Submit" />
    </FormShell>
  );
}

export default DPambulance;
